import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { AlertTriangle, GitMerge, Loader2, EyeOff, ShieldCheck, AlertOctagon, ExternalLink } from 'lucide-react';
import { formatDuplicateDate } from './duplicateHelpers';

const TIER_META = {
  exact: {
    label: 'Exact Duplicate',
    Icon: ShieldCheck,
    cardClass: 'border-rose-300 bg-rose-50/60',
    iconClass: 'text-rose-700',
    headerClass: 'text-rose-800',
    badgeClass: 'bg-rose-100 text-rose-800 border-rose-300',
    helper: 'Same name + DOB + parent — auto-merge eligible',
  },
  strong: {
    label: 'Strong Match',
    Icon: AlertOctagon,
    cardClass: 'border-amber-300 bg-amber-50/60',
    iconClass: 'text-amber-700',
    headerClass: 'text-amber-800',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-300',
    helper: 'Same name + DOB · parent differs/missing — review before merge',
  },
  possible: {
    label: 'Possible Duplicate',
    Icon: AlertTriangle,
    cardClass: 'border-slate-200 bg-slate-50/60',
    iconClass: 'text-slate-500',
    headerClass: 'text-slate-700',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-300',
    helper: 'Soft match — review only',
  },
};

export const DuplicateCard = ({ type, dup, onMerge, onIgnore, ignoring }) => {
  const item1 = type === 'player' ? dup.player1 : dup.parent1;
  const item2 = type === 'player' ? dup.player2 : dup.parent2;
  const nameField = type === 'player' ? 'full_name' : 'name';
  const idLabel = type === 'player' ? 'Player ID' : 'Parent ID';

  if (!item1 || !item2) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="p-4">
          <p className="text-red-600">Error: Missing data for comparison</p>
        </CardContent>
      </Card>
    );
  }

  const pairKey = `${item1.id}-${item2.id}`;
  // Player tab now ships `match_tier`; parent tab still uses confidence-only
  // styling. Map confidence → tier as a fallback so the design stays
  // consistent across both tabs.
  const tier = dup.match_tier
    || (dup.match_confidence >= 90 ? 'exact'
       : dup.match_confidence >= 75 ? 'strong'
       : 'possible');
  const meta = TIER_META[tier] || TIER_META.possible;
  const TierIcon = meta.Icon;

  return (
    <Card className={meta.cardClass} data-testid={`dup-card-${tier}-${item1.id}-${item2.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <TierIcon className={`h-4 w-4 ${meta.iconClass}`} />
              <span className={`font-medium ${meta.headerClass}`}>{meta.label}</span>
              {typeof dup.match_confidence === 'number' && (
                <Badge
                  className={meta.badgeClass}
                  data-testid={`dup-confidence-${item1.id}-${item2.id}`}
                >
                  {dup.match_confidence}% match
                </Badge>
              )}
              {dup.auto_merge_eligible && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] uppercase tracking-wide">
                  Auto-merge eligible
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-600 mb-2">{meta.helper}</p>

            <div className="grid grid-cols-2 gap-4 mt-3">
              {[item1, item2].map((item, idx) => {
                // Build a profile-link target so the admin can verify the
                // record is real before merging.
                const profileHref = type === 'player'
                  ? `/admin/players/${item.id}`
                  : (item.role === 'customer'
                      ? `/admin/customers/${item.id}`
                      : `/admin/parents/${item.id}`);
                const shortId = (item.id || '').slice(0, 8);
                return (
                <div key={idx} className="p-3 bg-white rounded-lg border" data-testid={`dup-side-${idx}-${item.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={profileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                      data-testid={`dup-name-link-${item.id}`}
                    >
                      {item[nameField] || 'Unknown'}
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                    {type === 'parent' && item.role && (
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                          item.role === 'customer'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                        data-testid={`role-badge-${item.id}`}
                      >
                        {item.role}
                      </span>
                    )}
                    {item.numeric_id && (
                      <span className="text-xs text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                        {idLabel}: {item.numeric_id}
                      </span>
                    )}
                    {/* Always show the system UUID prefix so admins can
                        visually confirm two cards are not the same record. */}
                    <span
                      className="text-[10px] text-slate-400 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200"
                      title={item.id}
                      data-testid={`dup-uuid-${idx}-${item.id}`}
                    >
                      {shortId}…
                    </span>
                  </div>
                  {type === 'player' ? (
                    <>
                      <p className="text-sm text-slate-500">DOB: {item.date_of_birth || 'N/A'}</p>
                      <p className="text-sm text-slate-500">Created: {formatDuplicateDate(item.created_at)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500">{item.email || 'No email'}</p>
                      <p className="text-sm text-slate-500">{item.phone || 'No phone'}</p>
                    </>
                  )}
                </div>
                );
              })}
            </div>

            <div className="flex gap-2 mt-3 flex-wrap">
              {dup.reasons?.same_first_name && dup.reasons?.same_last_name && (
                <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">Exact Name</Badge>
              )}
              {!(dup.reasons?.same_first_name && dup.reasons?.same_last_name)
                && dup.reasons?.name_similarity > 0.5 && (
                <Badge variant="outline" className="text-xs">
                  Name: {Math.round(dup.reasons.name_similarity * 100)}% similar
                </Badge>
              )}
              {dup.reasons?.same_parent && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Same Parent</Badge>
              )}
              {dup.reasons?.same_dob && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Same DOB</Badge>
              )}
              {dup.reasons?.same_phone && (
                <Badge variant="outline" className="text-xs bg-purple-50">Same Phone</Badge>
              )}
              {dup.reasons?.same_email && (
                <Badge variant="outline" className="text-xs bg-orange-50">Same Email</Badge>
              )}
              {!dup.reasons?.same_email && dup.reasons?.email_similarity >= 0.7 && (
                <Badge variant="outline" className="text-xs bg-orange-50/60">
                  Email: {Math.round(dup.reasons.email_similarity * 100)}% similar
                </Badge>
              )}
              {dup.reasons?.same_email_domain && (
                <Badge variant="outline" className="text-xs bg-yellow-50">Same Domain</Badge>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              onClick={() => onMerge(type, item1.id, item2.id)}
              disabled={item1.id === item2.id}
              className={tier === 'exact'
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-amber-600 hover:bg-amber-700'}
              data-testid={`merge-btn-${item1.id}-${item2.id}`}
              title={item1.id === item2.id ? 'Same record on both sides — cannot merge' : undefined}
            >
              <GitMerge className="h-4 w-4 mr-1" />
              {item1.id === item2.id ? 'Same record' : 'Merge'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onIgnore(item1.id, item2.id, type)}
              disabled={ignoring[pairKey]}
              className="text-slate-600 hover:text-slate-900"
              data-testid={`ignore-btn-${item1.id}-${item2.id}`}
            >
              {ignoring[pairKey] ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <EyeOff className="h-4 w-4 mr-1" />
              )}
              Ignore
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

